import React from "react";
import numeral from "numeral";
import { Column, Table, AutoSizer, SortIndicator, SortDirection } from "react-virtualized";
import { Popover, OverlayTrigger } from "react-bootstrap";

import styles from "./style.module.css";

const METRICS_COLUMNS = [
  { dataKey: "name", label: "Model Name" },
  { dataKey: "model_type", label: "Primary Model" },
  { dataKey: "trend_model", label: "Trend Model" },
  { dataKey: "model_seasonal", label: "Seasonal Model" },
  { dataKey: "other_factors", label: "Other Factors" },
  { dataKey: "error_distribution", label: "Error Distribution" },
  { dataKey: "mean_absolute_error", label: "Mean Absolute Error" },
  { dataKey: "MEAU", label: "Median Abs. Error (Inc. Uncertainty)" }
];

const TOOLTIPS = {
  model_type: {
    ["Bayesian Structural Time Series"]:
      "The primary model used in Aible’s forecasting system is a “Bayesian structural time series model” (bsts).  Bsts models are modular, like legos.  They have several options that can be turned on and off, and several components that can be pieced together to describe different types of patterns in the data."
  },
  model_number: false,
  trend_model: {
    ["Local Level"]:
      "This model is useful when your data is fairly stable.  It might move up a bit, or down a bit, but it’s not on a mission in either direction.  For this model, the best guess about tomorrow’s value is today’s value.",
    ["Local Linear Trend"]:
      "This model is useful when your data is moving either up or down, and you expect the growth to keep going (at least in the short term).  For this model, the best guess about tomorrow’s value is today’s value, plus a bump up or down to reflect how the trend has been behaving recently.",
    ["Semilocal Linear Trend"]:
      "This is a local linear trend model that leans more heavily on longer term trends.  This model is especially useful if you plan on forecasting far into the future.",
    ["Student Local Linear Trend"]: `This is a local linear trend model that allows for sudden violent jumps in either the level or the slope of the trend.  This model is useful if your data includes one or more "regime changes."`
  },
  model_seasonal: {
    ["Seasonal"]: `This model is essentially a set of dummy variables for cyclic behavior.  There is one “seasonal factor” for each period (e.g. each day of the week, or each month of the year).  The seasonal factors can evolve slowly over time.`,
    ["Trig"]: `This model describes cyclic variation in terms of a small number of sines and cosines.  It is useful when describing seasonal variation over long time periods (e.g. an annual effect with daily data).`
  },
  error_distribution: {
    ["Gaussian"]: `Gaussian errors are typical model errors that follow a normal distribution.`,
    ["Student"]: `This error distribution is robust to outliers.  If your time series contains occasional very large (or very small) observations.  If you see the [student] tag on a model name it means that the noise around the main time series component is being modeled with a Student T distribution.`
  },
  other_factors: {
    ["AAR"]: `AAR: An “auto-ar” model.  This model helps pick up “little wiggles” in the data that aren’t just noise, but which are a little too wiggly to be part of the trend.  You can think of it as a “helper” for the trend model.`
  }
};

function getTooltip(dataKey, cellData) {
  let toolTip = cellData;
  if (TOOLTIPS[dataKey] === false) {
    toolTip = undefined;
  } else if (typeof TOOLTIPS[dataKey] === "string") {
    toolTip = TOOLTIPS[dataKey];
  } else if (Array.isArray(cellData)) {
    toolTip = cellData.map((elem) => getTooltip(dataKey, elem));
    if (!toolTip.find((e) => e !== undefined)) {
      toolTip = undefined;
    }
  } else if (typeof TOOLTIPS[dataKey] === "object") {
    if (TOOLTIPS[dataKey][cellData] === false) {
      toolTip = undefined;
    } else {
      toolTip = TOOLTIPS[dataKey][cellData];
    }
  }
  return toolTip;
}

export default class MetricsTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sortBy: "mean_absolute_error",
      sortDirection: SortDirection.ASC,
      prevSortedData: undefined
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { data } = this.props;
    const prevData = prevProps.data;

    const { prevSortedData } = this.state;

    const dataLength = data ? data.length : 0;
    const prevDataLength = prevData ? prevData.length : 0;

    if (prevSortedData && dataLength !== prevDataLength) {
      this.setState({ prevSortedData: undefined });
    }
  }

  headerRowRenderer({ className, columns, width, style }) {
    return (
      <div className={styles.metricsTableHeader} style={{ width: width - 15 }} role="row">
        {columns}
      </div>
    );
  }

  headerRenderer({ columnData, dataKey, disableSort, label, sortBy, sortDirection }) {
    const showTooltip = false;

    return (
      <OverlayTrigger
        placement="top"
        trigger={showTooltip ? ["hover"] : []}
        rootClose
        overlay={
          <Popover id="popover-positioned-top">
            <div className={styles.tileOverlay}>{label}</div>
          </Popover>
        }
      >
        <div className={styles.metricsTableHeaderCell}>
          <strong>{label}</strong>
          {sortBy === dataKey && <SortIndicator sortDirection={sortDirection} />}
        </div>
      </OverlayTrigger>
    );
  }

  cellRenderer({ columnData, cellData, dataKey, rowData, rowIndex }) {
    const { data } = this.props;
    const optimalModelData = data && data[0];
    const isOptimal = optimalModelData && optimalModelData.id === rowData.id && dataKey === "model_number";
    return <CellToolTip cellData={cellData} dataKey={dataKey} isOptimal={isOptimal} />;
  }

  rowStyleFormat(row, sortedData) {
    const { selection } = this.props;

    if (row.index < 0) return;
    if (selection == sortedData[row.index].value) {
      return {
        backgroundColor: "#E6E6FC"
      };
    }
    return {
      cursor: "pointer"
    };
  }

  handleSelectModel(models, index) {
    const { updateModelSelection } = this.props;
    updateModelSelection(models[index].id);
  }

  calculateColumnWidth(width, numColumns, dataKey) {
    // These MUST cancel each other out
    const WIDTH_SCALARS = {
      model_type: 1.5,
      model_seasonal: 0.5,

      trend_model: 1.25,
      error_distribution: 0.75,

      name: 1.5,
      other_factors: 0.5
    };

    const retScalar = WIDTH_SCALARS[dataKey] !== undefined ? WIDTH_SCALARS[dataKey] : 1;

    return (width / numColumns) * retScalar;
  }

  render() {
    const { data } = this.props;
    const { sortBy, sortDirection, prevSortedData } = this.state;

    const optimalModelData = data && data[0];

    const sortScalar = sortDirection === SortDirection.DESC ? -1 : 1;
    let sortedData = (prevSortedData === undefined ? data : prevSortedData)
      .map((elem, i) => ({ ...elem, currIndex: i }))
      .sort((a, b) => {
        let retA = isNaN(a[sortBy]) ? a[sortBy] : Number(a[sortBy]);
        let retB = isNaN(b[sortBy]) ? b[sortBy] : Number(b[sortBy]);
        if (retA < retB) {
          return sortScalar * -1;
        } else if (retA > retB) {
          return sortScalar * 1;
        } else {
          return a.currIndex !== undefined && b.currIndex !== undefined ? a.currIndex - b.currIndex : 0;
        }
      });

    const numColumns = METRICS_COLUMNS.length;

    return (
      <AutoSizer>
        {({ width, height }) => (
          <Table
            width={width}
            height={height}
            headerHeight={40}
            headerRowRenderer={(args) => this.headerRowRenderer({ ...args, width: width })}
            rowClassName={styles.metricsTableRow}
            rowHeight={50}
            rowCount={data.length}
            rowStyle={(row) => this.rowStyleFormat(row, sortedData)}
            rowGetter={({ index }) => sortedData[index]}
            onRowClick={({ index }) => this.handleSelectModel(sortedData, index)}
            sort={({ sortBy, sortDirection }) => this.setState({ sortBy, sortDirection, prevSortedData: sortedData })}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {METRICS_COLUMNS.map((metric) => (
              <Column
                key={"model-metric-" + metric.value}
                label={metric.label}
                dataKey={metric.dataKey}
                width={this.calculateColumnWidth(width, numColumns, metric.dataKey)}
                headerRenderer={this.headerRenderer.bind(this)}
                cellRenderer={this.cellRenderer.bind(this)}
              />
            ))}
          </Table>
        )}
      </AutoSizer>
    );
  }
}

class Badge extends React.PureComponent {
  render() {
    const { text } = this.props;
    return <div className={styles.badge}>{text}</div>;
  }
}

class CellToolTip extends React.PureComponent {
  formatCellData(cellData) {
    return isFinite(cellData) ? numeral(cellData).format("0.[00]a") : cellData;
  }

  render() {
    const { cellData, dataKey, isOptimal } = this.props;

    const toolTip = getTooltip(dataKey, cellData);

    let toolTipTitle = toolTip === cellData ? undefined : cellData;
    let cellContent = cellData;
    if (Array.isArray(cellData)) {
      cellContent = cellData.map((e) => this.formatCellData(e)).join(", ");
      toolTipTitle = cellContent;
    } else {
      cellContent = this.formatCellData(cellData);
    }

    return (
      <div className={styles.metricsCellWrapper}>
        <OverlayTrigger
          placement="top"
          trigger={toolTip !== undefined ? ["hover", "click"] : []}
          rootClose
          overlay={
            <Popover id="popover-positioned-top" title={toolTipTitle}>
              <div className={styles.tooltipOverlay}>
                {Array.isArray(toolTip) ? (
                  toolTip.map((t, i) => (
                    <div key={"tooltip-" + i} className={styles.toolTipElem}>
                      {t}
                    </div>
                  ))
                ) : (
                  <div className={styles.toolTipElem}>{toolTip}</div>
                )}
              </div>
            </Popover>
          }
        >
          <div className={styles.metricsCell}>
            <span>{cellContent}</span>
            {isOptimal ? <Badge text={"OPTIMAL"} /> : <React.Fragment />}
          </div>
        </OverlayTrigger>
      </div>
    );
  }
}
